import { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { MONTH_LABELS, formatWon } from "Components/Sales/salesFormat";

const formatTick = (value) => `${(value / 10000).toLocaleString("ko-KR")}만`;
const formatPct = (value) => `${Math.round(value)}%`;

export const buildBreakdownRows = (months, tags, mode) => {
  const byMonth = new Map((months || []).map((m) => [m.month, m]));
  return MONTH_LABELS.map((label, i) => {
    const m = byMonth.get(i + 1);
    const row = { month: i + 1, label, total: m ? m.total : 0 };
    const amounts = new Map((m?.by_tag || []).map((b) => [b.tag_id, b.amount]));
    tags.forEach((tag) => {
      const amount = amounts.get(tag.id) || 0;
      row[`tag_${tag.id}`] = mode === "pct" && row.total ? (amount / row.total) * 100 : amount;
      row[`raw_${tag.id}`] = amount;
    });
    return row;
  });
};

const TooltipContent = ({ active, payload, label, tags, mode }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{ background: "#111827", color: "#f9fafb", padding: "8px 10px", borderRadius: "6px", fontSize: "12px" }}>
      <div style={{ fontWeight: 600, marginBottom: "4px" }}>{label} · {formatWon(row.total)}</div>
      {tags.filter((t) => row[`raw_${t.id}`] > 0).map((t) => (
        <div key={t.id} style={{ display: "flex", justifyContent: "space-between", gap: "14px" }}>
          <span><i style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: t.color, marginRight: 6 }} />{t.name}</span>
          <span>{formatWon(row[`raw_${t.id}`])}{row.total ? ` · ${Math.round((row[`raw_${t.id}`] / row.total) * 100)}%` : ""}</span>
        </div>
      ))}
      {mode === "pct" && <div style={{ marginTop: 4, color: "#9ca3af" }}>비율 기준 표시</div>}
    </div>
  );
};

const BreakdownBarChart = ({ months, tags, mode, selectedMonth, onSelectMonth }) => {
  const rows = useMemo(() => buildBreakdownRows(months, tags, mode), [months, tags, mode]);

  return (
    <ResponsiveContainer width="100%" height={340}>
      <BarChart
        data={rows}
        margin={{ top: 16, right: 16, bottom: 8, left: 8 }}
        barSize={24}
        onClick={(state) => {
          if (state && state.activeTooltipIndex != null) onSelectMonth(state.activeTooltipIndex + 1);
        }}
        style={{ cursor: "pointer" }}
      >
        <CartesianGrid stroke="#e5e7eb" vertical={false} />
        <XAxis
          dataKey="label"
          tick={({ x, y, payload, index }) => (
            <text x={x} y={y + 12} textAnchor="middle" fontSize={12}
              fill={index + 1 === selectedMonth ? "#1f2937" : "#6b7280"}
              fontWeight={index + 1 === selectedMonth ? 700 : 400}>
              {payload.value}
            </text>
          )}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={mode === "pct" ? formatPct : formatTick}
          domain={mode === "pct" ? [0, 100] : [0, "auto"]}
          tick={{ fontSize: 11, fill: "#6b7280" }}
          axisLine={false}
          tickLine={false}
          width={56}
        />
        <Tooltip content={<TooltipContent tags={tags} mode={mode} />} cursor={{ fill: "#eff6ff" }} />
        <Legend wrapperStyle={{ fontSize: 13 }} />
        {tags.map((tag, idx) => (
          <Bar
            key={tag.id}
            dataKey={`tag_${tag.id}`}
            name={tag.name}
            stackId="month"
            fill={tag.color}
            radius={idx === tags.length - 1 ? [3, 3, 0, 0] : 0}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BreakdownBarChart;
